// import packages
import React from 'react'
import { useHistory } from 'react-router-dom'

// import apis
import { apiLoadAllPrintRequests,
  apiCompletePrintRequest,
  apiDenyPrintRequest,
  apiReadyPrintRequest ,
  apiDownloadedPrintRequest,
  apiReopenPrintRequest,
  apiDeletePrintRequest  } from 'api/print_requests'

// import redux, selectors, actions
import { useSelector, useDispatch } from 'react-redux'
import { loadPrintRequests, updatePrintRequest, deletePrintRequest } from 'redux/actions/print_requests'

// import contexts
import { FetchContext } from 'contexts/FetchContext'
import { NavContext } from 'contexts/NavContext'

// import hooks

// import utilities

// import common elements
import { Table, Button, ButtonToolbar, Form } from 'react-bootstrap'
import { PageLayout } from 'components/elements/Layout'
import { Icon } from 'components/elements/Icons'

// import components
import AuthenticatedNav from 'components/navs/AuthenticatedNav'

// import styles

// local constants

// --------------------

const PrintRequests = () => {
  const { authAxios } = React.useContext(FetchContext)
  const history = useHistory()
  const requests = useSelector(state => Object.values(state.print_requests))
  const requestCount = requests.length
  const reduxDispatch = useDispatch()
  const { setPdfCardId } = React.useContext(NavContext)

  const [sortBy, setSortBy] = React.useState('name')
  const [sortAsc, setSortAsc] = React.useState(true)
  const [searchText, setSearchText] = React.useState('')
  const [showAll, setShowAll] = React.useState(false)

  const [loadedRequests, setLoadedRequests] = React.useState(false)

  React.useEffect(() => {
    const abortController = new AbortController()

    if (!loadedRequests) {
      apiLoadAllPrintRequests(authAxios, {
        signal: abortController.signal,
        successDispatch: [reduxDispatch, loadPrintRequests],
        success: response => {
          setLoadedRequests(true)
        },
      })
    }
    return () => {
      abortController.abort()
    }
  }, [loadedRequests, authAxios, reduxDispatch])

  React.useEffect(() => {
    setSortAsc(sortBy === 'latest' ? false : true)
  }, [sortBy])

  const [DOWN, UP] = sortAsc ? [-1, 1] : [1, -1]

  const reload = () => setLoadedRequests(false)

  const view = request => {
    history.push(`/card/${request.card_id}`)
  }

  const download = async (request) => {
    setPdfCardId(request.card_id)
    apiDownloadedPrintRequest(authAxios, request.id, {
      successDispatch: [reduxDispatch, updatePrintRequest],
      successMsg: 'Marked as downloaded!',
    })
  }

  const markComplete = request => {
    apiCompletePrintRequest(authAxios, request.id, {
      successDispatch: [reduxDispatch, updatePrintRequest],
      successMsg: 'Done!',
    })
  }

  const markReady = request => {
    apiReadyPrintRequest(authAxios, request.id, {
      successDispatch: [reduxDispatch, updatePrintRequest],
      successMsg: 'Marked as ready for pickup!',
    })
  }

  const denyRequest = (request, denialComment) => {
    apiDenyPrintRequest(authAxios, request.id, denialComment, {
      successDispatch: [reduxDispatch, updatePrintRequest],
      successMsg: 'Request denied!',
    })
  }

  const reopenRequest = request => {
    apiReopenPrintRequest(authAxios, request.id, {
      successDispatch: [reduxDispatch, updatePrintRequest],
      successMsg: 'Request reopened!',
    })
  }

    const cancelPrintRequest = (printRequestId) => {
      apiDeletePrintRequest(authAxios, printRequestId, {
        successDispatch: [reduxDispatch, deletePrintRequest],
        successMsg: 'Print request(s) cancelled',
        errorMsg: 'Failed to cancel print request',
      })
    }

  const sortTitle = () => {
    if (sortBy === 'title') {
      setSortAsc(t => !t)
    } else {
      setSortBy('title')
    }
  }
  const sortRequestor = () => {
    if (sortBy === 'requestor') {
      setSortAsc(t => !t)
    } else {
      setSortBy('requestor')
    }
  }
  const sortCreated = () => {
    if (sortBy === 'created') {
      setSortAsc(t => !t)
    } else {
      setSortBy('created')
    }
  }

  const titleSort = (a, b) => {
    const t1 = a.card_title
    const t2 = b.card_title
    return t1 < t2 ? DOWN : t1 > t2 ? UP : 0
  }
  const requestorSort = (a, b) => {
    const c1 = a.user_full_name
    const c2 = b.user_full_name
    return c1 < c2 ? DOWN : c1 > c2 ? UP : 0
  }
  const createdSort = (a, b) => {
    const l1 = a.created_at || ''
    const l2 = b.created_at || ''
    return l1 < l2 ? DOWN : l1 > l2 ? UP : 0
  }

  const currentSorter = () => {
    if (sortBy === 'title') {
      return titleSort
    } else if (sortBy === 'requestor') {
      return requestorSort
    } else {
      return createdSort
    }
  }

  const matchesSearch = (request) => {
    const requestor = request.user_full_name.toLowerCase()
    const title = request.card_title.toLowerCase()
    const search = searchText.toLowerCase()
    if (!showAll && (request.status === 'complete' || request.status === 'denied')) {
      return false
    }
    return (
      requestor.includes(search) || title.includes(search)
    )
  }
  const searchCount = requests.filter(c => matchesSearch(c)).length

  const filteredRequests = requests.filter(request => matchesSearch(request))

  const sortedRequests = filteredRequests.sort(currentSorter())

  const sortIcon = field => {
    return field === sortBy ? (
      sortAsc ? (
        <Icon spaceAfter icon='up' />
      ) : (
        <Icon spaceAfter icon='down' />
      )
    ) : null
  }

  const actions = [<Button key='reload' size='sm' variant='outline-secondary' onClick={reload}>Reload</Button>,
    <Button key='showAll' size='sm' variant='outline-secondary' onClick={() => setShowAll(!showAll)}>
      {showAll ? 'Hide Completed' : 'Show Completed'}
    </Button>
  ]

  return (
    <PageLayout title='Print Requests' actions={actions}>
      <AuthenticatedNav />
      <p>Request count: {requestCount}</p>
      <div className='d-flex justify-flex-start align-items-baseline mt-0' style={{ gap: '1rem' }}>
        <div>

            <Form.Control
              name='search'
                type='text'
                size='sm'
              placeholder='Search...'
              onChange={e => setSearchText(e.target.value)}
              value={searchText}
            />

          </div>
        <div>
          <Button
            size='sm'
            variant='outline-secondary'
            onClick={() => setSearchText('')}
          >
            Clear Search
          </Button>
          </div>
          <div>Matches: {searchCount}/{requestCount}</div>
      </div>
      {loadedRequests && (
        <Table striped bordered hover size='sm'>
          <thead>
            <tr>
              <th onClick={sortTitle}>{sortIcon('title')}Title</th>
              <th onClick={sortRequestor}>{sortIcon('requestor')}Requestor</th>
              <th onClick={sortCreated}>{sortIcon('created')}Created at</th>
              <th>Ready at</th>
              <th>Completed at</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedRequests.map(request => (
              <tr key={request.id}>
                <td>{request.card_title}{request.status === 'denied' && <div>Denied: {request.denial_comment}</div>}</td>
                <td>{request.user_full_name}</td>
                <td><small>{request.created_at}</small></td>
                <td><small>{request.ready_at}</small></td>
                <td><small>{request.completed_at}</small></td>
                <td>{request.status}</td>
                <td>
                  <ButtonToolbar>
                    <Button size='sm' variant='outline-secondary' onClick={() => view(request)}>
                      View Card
                    </Button>

                    {request.status === 'pending' && (
                      <Button size='sm' variant='secondary' onClick={() => download(request)}>
                        Download
                      </Button>
                    )}
                    {(request.status === 'pending' || request.status === 'downloaded') && (
                      <Button size='sm' variant='secondary' onClick={() => markReady(request)}>
                        Ready for Pickup
                      </Button>
                    )}
                  {(request.status === 'pending' || request.status === 'downloaded' || request.status === 'ready') && (
                    <Button size='sm' variant='secondary' onClick={() => markComplete(request)}>
                      Complete
                    </Button>
                    )}
                    {(request.status ==='pending' || request.status === 'downloaded') && (
                      <Button size='sm' variant='outline-secondary' onClick={() => denyRequest(request, prompt('Enter denial reason:'))}>
                        Deny
                      </Button>
                    ) }
                    {request.status === 'denied' && (
                      <Button size='sm' variant='outline-secondary' onClick={() => reopenRequest(request)}>
                        Reopen
                      </Button>
                    )}
                    <Button size='sm' variant='outline-danger' onClick={()=>cancelPrintRequest(request.id)}>
                      Delete
                    </Button>
                  </ButtonToolbar>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageLayout>
  )
}

export default PrintRequests
